import type { Client, ConnectionState, Media } from '@twilio/conversations';
import type { ConversationPreview } from '@mfw/common';

import { MAX_CHAT_ATTRIBUTES_LENGTH } from '@mfw/common';
import * as Sentry from '@sentry/react';

import type {
  FetchInfiniteConversationMessagesArgs,
  UpdateLastReadMessageIndexArgs,
  CreateChatReturnType,
  SendMessageArgs,
} from './types/conversations.types';
import type { MessageMediaInterface } from '../hooks/chat/useMessageMedia';
import type { TwilioTokenLifecycle } from './twilioTokenLifecycle';
import type { BaseApiResponse } from './types/api.types';

import { createTwilioTokenLifecycle } from './twilioTokenLifecycle';
import { markOwnedByChatRecovery } from './chatRecoveryOwnership';
import { getChatMessageInputIssue } from './chatMessageValidation';
import { decodeJwtExp, destroy, patch, post, get } from './axios';
import { InternalServerError } from './http';
interface TwilioTokenResponse {
  token: string;
}

/**
 * The single error path every SDK call in this module rethrows through — so it
 * is also where the lifecycle learns that the socket underneath is gone.
 * Twilsock has no heartbeat, so a transport the service has stopped answering
 * keeps reporting `connected`; only a real operation failing to be delivered
 * reveals it. The lifecycle decides what counts (see `isDeadTransportError`)
 * and rebuilds on its own backoff; the error still propagates untouched to the
 * caller, which is what surfaces it in the UI. A failure the lifecycle says it
 * is already recovering from is marked as such, so the global handlers do not
 * report each symptom of one outage (see `chatRecoveryOwnership`).
 *
 * SDK calls ONLY. The REST calls in this module (`createChat`, the group
 * roster/name edits, `fetchConversationPreviews`) let their errors propagate
 * on their own: a malformed backend answer is not something rebuilding the
 * Twilio client can fix, and routing it here had it marked as a recovery
 * symptom — kept out of Sentry — whenever a rebuild happened to be queued.
 *
 * Declared as a FUNCTION, not an arrow assigned to a const: TypeScript only
 * lets a `never`-returning call end control flow when the callee is a function
 * declaration or a name with an explicit type annotation. As an arrow it stayed
 * `void` to the checker, which is what put a phantom `| undefined` on six public
 * return types here — a value none of them can ever produce.
 */
function handleErrors(e: unknown): never {
  if (lifecycle?.reportOperationFailure(e)) markOwnedByChatRecovery(e);
  throw e;
}

// -------------------------------------------------------------------------
// Singleton lifecycle
// The lifecycle owns the client and its access token: it refreshes the token
// before expiry, rebuilds the client if the session dies anyway (expired
// token, denied connection) and catches up after background-tab throttling.
// -------------------------------------------------------------------------
let lifecycle: TwilioTokenLifecycle<Client> | null = null;

/**
 * Bumped by every `shutdownTwilioClient()`. An initialize that was still
 * loading the SDK when the chat was torn down finds it changed and stops: no
 * lifecycle exists yet for the shutdown to end, and building one afterwards
 * left a live client nobody owned — whose `connected` state then made the
 * next mount return early without a single status callback.
 */
let generation = 0;

async function fetchTwilioToken() {
  const res = await get<TwilioTokenResponse>('/v2/chat/get-token');
  const token = res?.token;
  if (!token) {
    throw new InternalServerError({
      message: 'Twilio token retrieval failed: No token received.',
    });
  }
  return token;
}

// -------------------------------------------------------------------------
// Initialization Function
// This must be called *once* when the user is authenticated/available.
// -------------------------------------------------------------------------
export async function initializeTwilioService(
  onStatusChange: (status: ConnectionState) => void,
  /** The client was replaced: every SDK object obtained from it is dead. */
  onClientDiscarded: () => void = () => {},
): Promise<void> {
  // The SDK (with twilio-sync and twilsock, ~900 KB) loads on first chat use,
  // not with the app shell: this module is reachable from `queryKeys`, which
  // every screen imports. Awaited FIRST so everything below still runs in one
  // synchronous stretch — two concurrent calls keep seeing each other's
  // lifecycle exactly as they did with the static import. A shutdown during
  // the load is the one thing the await lets in — see `generation`.
  const startedIn = generation;
  const { Client: TwilioClient } = await import('@twilio/conversations');
  if (startedIn !== generation) return;
  const currentState = lifecycle?.getClient()?.connectionState;
  if (currentState === 'connected' || currentState === 'connecting') {
    return;
  }
  if (lifecycle) {
    // Deliberately NOT awaited. The lifecycle marks itself shut down and drops
    // its client, listeners and timers synchronously, so it is inert the moment
    // this returns — while the SDK call it wraps can park indefinitely: a
    // Conversations client that is still initializing cannot be cancelled
    // (`Client.shutdown()` waits on `_ensureReady`), and awaiting that held the
    // replacement connection for the full 30 s twilsock request timeout.
    lifecycle.shutdown();
    lifecycle = null;
  }

  onStatusChange('connecting');
  const created = createTwilioTokenLifecycle({
    clearTimer: (id) => window.clearTimeout(id as number),
    createClient: (token) => new TwilioClient(token),
    decodeTokenExp: decodeJwtExp,
    fetchToken: fetchTwilioToken,
    // eslint-disable-next-line no-console
    log: (message, error) => console.error(message, error),
    now: () => Date.now(),
    onClientDiscarded,
    onStatusChange: (status) => onStatusChange(status as ConnectionState),
    // The one Sentry signal for a chat outage: every operation failing during
    // it is marked as owned by the recovery and stays out of Sentry.
    reportPersistentFailure: (error) =>
      Sentry.captureMessage('Twilio chat client keeps failing to rebuild', {
        extra: {
          cause: error instanceof Error ? error.message : String(error),
        },
        level: 'warning',
      }),
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    subscribeVisibility: (onVisible) => {
      const handler = () => {
        if (document.visibilityState === 'visible') onVisible();
      };
      document.addEventListener('visibilitychange', handler);
      return () => document.removeEventListener('visibilitychange', handler);
    },
  });
  lifecycle = created;

  try {
    await created.start();
  } catch (err) {
    await created.shutdown().catch(() => undefined);
    if (lifecycle === created) lifecycle = null;
    onStatusChange('error');
    handleErrors(err);
    return;
  }

  const client = created.getClient();
  if (client) onStatusChange(client.connectionState);
}

// -------------------------------------------------------------------------
// // Accessor Functions
// -------------------------------------------------------------------------
export function getTwilioClientOrNull(): Client | null {
  return lifecycle?.getClient() ?? null;
}

/**
 * Provides the initialized Twilio Client instance.
 * @returns The initialized Twilio Client.
 * @throws {InternalServerError} if the client has not been initialized.
 */
export function getTwilioClient(): Client {
  const client = getTwilioClientOrNull();
  if (!client) {
    throw new InternalServerError({
      message:
        'Twilio Client not initialized. Did you call initializeTwilioService()?',
    });
  }
  return client;
}

// -------------------------------------------------------------------------
// // Cleanup Function
// -------------------------------------------------------------------------
/**
 * Tears the chat client down. **Never rejects** — the lifecycle treats an SDK
 * shutdown failure as best-effort and logs it (see `twilioTokenLifecycle`), so
 * `useTwilioClient`'s effect cleanup may call this as a bare statement without
 * leaving an unobserved promise behind. Do NOT re-add a `.catch` at the call
 * sites; the guarantee belongs to the one chokepoint.
 */
export async function shutdownTwilioClient() {
  generation += 1;
  const current = lifecycle;
  lifecycle = null;
  if (current) {
    await current.shutdown();
  }
}

/**
 * Fetches a specific Twilio Conversation object by its unique SID.
 * @param id The Conversation SID (string).
 * @returns A promise resolving to the Conversation object.
 */
export async function getConversationBySid(id: null | string) {
  if (id === null) {
    return null;
  }
  try {
    const client = getTwilioClient();
    const conversation = await client.getConversationBySid(id);
    return conversation;
  } catch (error) {
    handleErrors(error);
  }
}

/**
 * Updates the 'last read message index' for a conversation on the server,
 * effectively marking all messages up to that index as read for the current user.
 * * @param conversation The Conversation object to be updated.
 * @param lastMessageIndex The index of the message that should be considered the last one read.
 * @returns A promise that resolves when the update is complete.
 */
export async function markConversationAsRead({
  conversation,
  lastMessageIndex,
}: UpdateLastReadMessageIndexArgs) {
  try {
    await conversation.updateLastReadMessageIndex(lastMessageIndex);
  } catch (error) {
    handleErrors(error);
  }
}
/**
 * Fetches messages from a given Conversation in a **paginated and infinite scroll fashion**.
 * It retrieves a specified page of messages, typically for use in a UI that loads more content
 * as the user scrolls up (fetching older messages).
 *
 * @param conversation The **Conversation** object from which to fetch messages.
 * @param pageParam The **index** of the newest message to include: the page is read
 * backwards from this anchor, inclusive. If undefined, it starts from the newest messages.
 * @param pageSize The maximum number of messages to fetch for the current page (limit).
 * @returns A promise that resolves to an **InfiniteMessagesPage** object containing:
 * - `messages`: An array of **Message** objects for the current page.
 * - `nextPageParam`: The `pageParam` to use for the *next* request (the index of the oldest message fetched minus 1),
 * or **undefined** if no more pages are available.
 */
export async function getInfiniteConversationMessages({
  conversation,
  pageParam,
  pageSize,
}: FetchInfiniteConversationMessagesArgs) {
  try {
    const paginator = await conversation!.getMessages(pageSize, pageParam);
    const messages = paginator.items;
    // Twilio rejects a negative anchor (Sentry MYFITWORLD-V2-81: "Argument 2
    // … but got -1"): once the conversation's first message (index 0) is
    // loaded there is no older page. The anchor is inclusive and read
    // backwards, so a gap left by deleted messages is skipped over — only when
    // nothing survives at or below it is the page empty, which ends paging.
    const oldest = messages[0]?.index;
    const nextPageParam =
      oldest !== undefined && oldest > 0 ? oldest - 1 : undefined;
    return {
      messages,
      nextPageParam,
    };
  } catch (error) {
    handleErrors(error);
  }
}
/**
 * Voice messages are recorded in whatever container/codec the sender's
 * browser's MediaRecorder supports (webm/opus on Chrome & Firefox, mp4/aac on
 * Safari) — and that raw file is what a recipient's <audio> element has to
 * decode. Safari's media engine cannot play a webm container at all, so a
 * message recorded on Chrome is silently unplayable for a Safari recipient.
 * This sends the raw recording to the backend, which transcodes it to
 * AAC/M4A — the one format every major browser decodes — before it's
 * attached to the Twilio message.
 */
export async function transcodeVoiceMessage(file: File): Promise<File> {
  const formData = new FormData();
  formData.set('file', file, file.name);

  // post()'s type signature assumes every response is the `{ data }` envelope
  // (BaseApiResponse<T>) — true for JSON endpoints, not for this one: with
  // responseType 'blob' axios hands back the raw Blob body untouched.
  const blob = (await post<Blob>('/v2/chat/transcode-voice-message', formData, {
    responseType: 'blob',
  })) as unknown as Blob | undefined;
  if (!blob) {
    throw new InternalServerError({
      message: 'Voice message transcoding failed: no file received.',
    });
  }

  const baseName = file.name.replace(/\.[^./]+$/, '');
  return new File([blob], `${baseName}.m4a`, { type: 'audio/mp4' });
}

/**
 * Every write is authorized by the backend in the selected organization.
 * Validate the complete batch first, then upload one attachment per message.
 * Errors propagate even when an earlier attachment was already delivered;
 * ChatsContext refreshes the history so that partial delivery stays visible.
 */
export async function sendMessage({
  conversation,
  organizationId,
  newMessage,
  selectedFiles,
  attributes,
}: SendMessageArgs) {
  if (!organizationId || !conversation.sid) {
    throw new Error(
      'An organization and conversation are required to send a message.',
    );
  }
  const serializedAttributes = JSON.stringify(attributes ?? {});
  if (
    getChatMessageInputIssue(newMessage, selectedFiles) ||
    serializedAttributes.length > MAX_CHAT_ATTRIBUTES_LENGTH
  ) {
    throw new Error('Message exceeds the supported upload limits.');
  }

  let lastIndex = 0;
  const attachments: Array<File | undefined> = selectedFiles.length
    ? selectedFiles
    : [undefined];
  for (const [index, file] of attachments.entries()) {
    const data = new FormData();
    data.set('body', index === 0 ? newMessage : '');
    data.set('attributes', serializedAttributes);
    if (file) data.set('file', file, file.name);
    const response = await post<{ index: number }>(
      `/v2/chat/${organizationId}/conversations/${conversation.sid}/messages`,
      data,
    );
    const sentIndex = response?.data?.index;
    if (
      typeof sentIndex !== 'number' ||
      !Number.isInteger(sentIndex) ||
      sentIndex < 0
    ) {
      throw new InternalServerError({
        message: 'Message delivery was not confirmed.',
      });
    }
    lastIndex = sentIndex;
  }
  return lastIndex;
}
/**
 * Categorizes Twilio message media into images, videos, audios, and documents.
 * Returns temporary URLs grouped by type.
 */
export async function getMessageMedia(media: Media[]) {
  try {
    const audios: MessageMediaInterface[] = [];
    const videos: MessageMediaInterface[] = [];
    const images: MessageMediaInterface[] = [];
    const documents: MessageMediaInterface[] = [];

    const results = await Promise.all(
      media.map(async (m) => {
        const url = await m.getContentTemporaryUrl();
        return {
          mediaItem: m,
          url,
        };
      }),
    );

    for (const result of results) {
      const { mediaItem: m, url } = result;
      if (!url) continue;

      const type = m.contentType;
      const item = { name: m.filename || '', url };

      if (type.startsWith('image/')) {
        images.push(item);
      }
      if (type.startsWith('video/')) {
        videos.push(item);
      }
      if (type.startsWith('audio/')) {
        audios.push(item);
      }
      if (type.startsWith('application/')) {
        documents.push(item);
      }
    }

    return { audios, documents, images, videos };
  } catch (error) {
    handleErrors(error);
  }
}
/**
 * Fetches the total number of unread messages across all conversations
 * the current user is a participant in.
 * @returns A promise resolving to the total unread message count (number).
 */
export async function getTotalUnreadMessagesCount() {
  try {
    const client = getTwilioClient();

    const paginator = await client.getSubscribedConversations();
    const conversations = paginator.items;

    let totalUnreadCount = 0;
    for (const conversation of conversations) {
      try {
        const count = await conversation.getUnreadMessagesCount();
        if (count !== null) {
          totalUnreadCount += count;
          continue;
        }
        // Twilio answers null until the participant has read anything in the
        // conversation (no read horizon yet) — every message is unread then,
        // which is exactly the state of a brand-new chat. Counting it as 0
        // is what hid the badge. Our own last message means nothing to read.
        const [lastMessage] = (await conversation.getMessages(1)).items;
        if (lastMessage && lastMessage.author !== client.user.identity) {
          totalUnreadCount += await conversation.getMessagesCount();
        }
      } catch (error) {
        handleErrors(error);
      }
    }
    return totalUnreadCount;
  } catch (error) {
    handleErrors(error);
  }
}
// ----------------------------------------------------------------------------------------------------------------------------------------------------------------------------
/**
 * Initiates the creation of a new chat/conversation on the backend service.
 * A `chatName` is only ever supplied through the group-creation flow (the 1:1
 * flow never passes one), so its presence is what routes the request to the
 * trainer/admin-gated group endpoint instead of the open 1:1 one.
 * @param participantsId An array of user IDs to include in the new chat.
 * @param chatName A friendly name for the new chat — required for a group, absent for a 1:1.
 * @param organizationId The organization the group's membership/permission check is scoped to.
 * @returns A promise resolving to the unique SID of the newly created Twilio Conversation.
 */
export async function createChat({
  chatName,
  organizationId,
  participantsId,
}: {
  participantsId: string[];
  chatName?: string;
  organizationId: string;
}) {
  const isGroup = Boolean(chatName);
  const url = isGroup
    ? `/v2/chat/${organizationId}/start-group-conversation`
    : `/v2/chat/${organizationId}/start-conversation`;
  const response = await post<CreateChatReturnType>(url, {
    chatName,
    participantsId,
  });
  if (!response?.data) throw new Error('No data returned');
  return response.data;
}
/** Renames a group. Each roster/name change applies instantly (no draft to
 * save), so this and the two functions below each cover exactly one action. */
export async function renameGroupConversation({
  chatName,
  conversationId,
  organizationId,
}: {
  conversationId: string;
  organizationId: string;
  chatName: string;
}) {
  await patch(`/v2/chat/${organizationId}/conversations/${conversationId}`, {
    chatName,
  });
}

/** Adds a single participant to a group immediately. */
export async function addGroupParticipant({
  conversationId,
  organizationId,
  participantId,
}: {
  conversationId: string;
  organizationId: string;
  participantId: string;
}) {
  await post(
    `/v2/chat/${organizationId}/conversations/${conversationId}/participants`,
    { participantId },
  );
}

/** Removes a single participant from a group immediately. The backend
 * refuses to remove the caller themselves or to drop the group below its
 * 2-other-participants floor. */
export async function removeGroupParticipant({
  conversationId,
  organizationId,
  participantId,
}: {
  conversationId: string;
  organizationId: string;
  participantId: string;
}) {
  await destroy(
    `/v2/chat/${organizationId}/conversations/${conversationId}/participants/${participantId}`,
  );
}
/**
 * Fetches a list of conversation previews (or summaries) for the current user
 * from the custom backend service.
 * * This endpoint is expected to return lightweight data for displaying a chat list,
 * including the conversation name, last message, and unread count.
 * * @returns A promise resolving to an array of ConversationPreview objects, or undefined on failure.
 */
export async function fetchConversationPreviews() {
  const response = await get<BaseApiResponse<ConversationPreview[]>>(
    '/v2/chat/conversations',
  );

  return response?.data;
}
